export type DocsProduct =
  | "poweron-pipelines"
  | "poweron-pro"
  | "glaci"
  | "nexum";

// Ordering and grouping are independent of the renderer and route implementation.
const pipelinePages = [
  {
    slug: "",
    file: "README.md",
    title: "Introduction",
    group: "Getting started",
  },
  {
    slug: "quickstart",
    file: "quickstart.md",
    title: "Quickstart",
    group: "Getting started",
  },
  {
    slug: "github-actions",
    file: "github-actions.md",
    title: "GitHub Actions",
    group: "Getting started",
  },
  {
    slug: "configuration",
    file: "configuration.md",
    title: "Configuration",
    group: "Getting started",
  },
  {
    slug: "changelog",
    file: "changelog.md",
    title: "Changelog",
    group: "Getting started",
  },
  {
    slug: "resources/examples",
    file: "resources/examples.md",
    title: "Examples",
    group: "Resources",
  },
  {
    slug: "resources/tasks",
    file: "resources/tasks.md",
    title: "Tasks",
    group: "Resources",
  },
  {
    slug: "resources/known-issues",
    file: "resources/known-issues.md",
    title: "Known Issues",
    group: "Resources",
  },
  {
    slug: "help-and-feedback",
    file: "help-and-feedback.md",
    title: "Help & Feedback",
    group: "Resources",
  },
];

export const docsPages = [
  {
    product: "nexum" as const,
    slug: "",
    file: "README.md",
    title: "Introduction",
    group: "Getting started",
  },
  {
    product: "nexum" as const,
    slug: "authentication",
    file: "authentication.md",
    title: "Authentication",
    group: "Getting started",
  },
  {
    product: "nexum" as const,
    slug: "quickstart",
    file: "quickstart.md",
    title: "Quickstart",
    group: "Getting started",
  },
  {
    product: "nexum" as const,
    slug: "mcp-quickstart",
    file: "mcp-quickstart.md",
    title: "MCP Quickstart",
    group: "Getting started",
  },
  {
    product: "nexum" as const,
    slug: "mcp-policies",
    file: "mcp-policies.md",
    title: "MCP Policies",
    group: "Getting started",
  },
  {
    product: "nexum" as const,
    slug: "naas-endpoints",
    file: "naas-endpoints.md",
    title: "NaaS Endpoints",
    group: "Getting started",
  },
  {
    product: "nexum" as const,
    slug: "capabilities",
    file: "capabilities.md",
    title: "Capabilities",
    group: "Getting started",
  },
  {
    product: "nexum" as const,
    slug: "changelog",
    file: "changelog.md",
    title: "Changelog",
    group: "Getting started",
  },
  {
    product: "nexum" as const,
    slug: "resources/symitar-playground",
    file: "resources/symitar-playground.md",
    title: "Symitar Playground",
    group: "Resources",
  },
  {
    product: "nexum" as const,
    slug: "resources/credentials",
    file: "resources/credentials.md",
    title: "Credentials",
    group: "SymXchange",
  },
  {
    product: "nexum" as const,
    slug: "resources/device-information",
    file: "resources/device-information.md",
    title: "Device Information",
    group: "SymXchange",
  },
  {
    product: "nexum" as const,
    slug: "resources/nil-field-support",
    file: "resources/nil-field-support.md",
    title: "Nil Field Support",
    group: "Resources",
  },
  {
    product: "nexum" as const,
    slug: "help-and-feedback",
    file: "help-and-feedback.md",
    title: "Help & Feedback",
    group: "Resources",
  },
  {
    product: "glaci" as const,
    slug: "",
    file: "README.md",
    title: "Introduction",
    group: "Getting started",
  },
  ...pipelinePages.map((page) => ({
    ...page,
    product: "poweron-pipelines" as const,
  })),
  ...[
    {
      slug: "",
      file: "README.md",
      title: "Introduction",
      group: "Getting started",
    },
    {
      slug: "quickstart",
      file: "quickstart.md",
      title: "Quickstart",
      group: "Getting started",
    },
    {
      slug: "capabilities",
      file: "capabilities.md",
      title: "Capabilities",
      group: "Getting started",
    },
    {
      slug: "changelog",
      file: "changelog.md",
      title: "Changelog",
      group: "Getting started",
    },
    {
      slug: "how-tos/setting-up-rsync-on-windows",
      file: "how-tos/setting-up-rsync-on-windows.md",
      title: "Setting up Rsync on Windows",
      group: "How To’s",
    },
    {
      slug: "resources/commands",
      file: "resources/commands.md",
      title: "Commands",
      group: "Resources",
    },
    {
      slug: "resources/known-issues",
      file: "resources/known-issues.md",
      title: "Known Issues",
      group: "Resources",
    },
    {
      slug: "resources/settings",
      file: "resources/settings.md",
      title: "Settings",
      group: "Resources",
    },
    {
      slug: "resources/help-and-feedback",
      file: "resources/help-and-feedback.md",
      title: "Help & Feedback",
      group: "Resources",
    },
  ].map((page) => ({ ...page, product: "poweron-pro" as const })),
];

export const docsProLinks = [
  {
    title: "VS Code Marketplace",
    url: "https://marketplace.visualstudio.com/items?itemName=libum.poweron-pro",
  },
  { title: "Libum GitHub", url: "https://github.com/libum-llc" },
];

// External sidebar destinations from PowerOn Pipelines' GitBook SUMMARY.md.
export const docsPipelineLinks = [
  {
    title: "PowerOn Pipelines",
    url: "https://marketplace.visualstudio.com/items?itemName=libum.poweron-pipelines",
  },
  {
    title: "Validate PowerOn",
    url: "https://github.com/marketplace/actions/validate-poweron",
  },
  {
    title: "Synchronize Symitar",
    url: "https://github.com/marketplace/actions/synchronize-symitar",
  },
  { title: "GitHub", url: "https://github.com/libum-llc" },
];

export function docsHref(slug: string, product: DocsProduct) {
  const base = `/docs/${product}`;
  return slug ? `${base}/${slug}` : base;
}

export type DocsSearchEntry = {
  breadcrumb?: string[];
  product: string;
  href: string;
  title: string;
  heading: string;
  text: string;
};

export const docsContexts = [
  { slug: "glaci", title: "Glaci", legacyUrl: null },
  {
    slug: "poweron-pro",
    title: "PowerOn Pro",
    legacyUrl: null,
  },
  {
    slug: "poweron-pipelines",
    title: "PowerOn Pipelines",
    legacyUrl: null,
  },
  {
    slug: "nexum",
    title: "NexumAPI",
    legacyUrl: null,
  },
];
